import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useMemo } from 'react'
import {
  type ReviewFormReport,
  type ReviewFormReporterSection as IReviewFormReporterSection,
  setReviewFormReporterSection,
} from 'services'
import { TextArea } from 'stories/components'
import { confirm } from 'utils'

export const ReviewFormReporterSection = ({
  data,
  setData,
  isLoading,
  setLoading,
  onEdit,
}: {
  data: IReviewFormReporterSection
  setData: (data: IReviewFormReporterSection) => void
  isLoading: boolean
  setLoading: (v: boolean) => void
  onEdit: (report: ReviewFormReport) => void
}) => {
  const orderOptions = useMemo(() => data.reports.map((_, i) => i.toString()), [data.reports])

  const persist = async (next: IReviewFormReporterSection) => {
    setLoading(true)
    try {
      await setReviewFormReporterSection(next)
      setData(next)
    } finally {
      setLoading(false)
    }
  }

  const onChangeLabel = (value: string) => setData({ ...data, label1: value })

  const onBlurLabel = async () => {
    await persist(data)
  }

  const onDelete = async (index: number) => {
    const content = (
      <div className="text-gray-600">
        Remove this report?
        <div className="text-gray-900 mt-2 text-sm">Title: {data.reports[index].title}</div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    const reports = cloneDeep(data.reports)
    reports.splice(index, 1)
    await persist({ ...data, reports })
  }

  const onChangeOrder = async (fromIndex: number, val: string) => {
    const toIndex = Number(val)
    if (Number.isNaN(toIndex) || toIndex === fromIndex) return
    const reports = cloneDeep(data.reports)
    const [moved] = reports.splice(fromIndex, 1)
    reports.splice(toIndex, 0, moved)
    await persist({ ...data, reports })
  }

  return (
    <div className="space-y-6">
      <div>
        <TextArea
          title="Section Header"
          value={data.label1}
          rows={3}
          disabled={isLoading}
          onChange={onChangeLabel}
          onBlur={onBlurLabel}
        />
      </div>

      <div className="relative overflow-auto shadow-md sm:rounded-lg">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3 w-[40px]">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Title
              </th>
              <th scope="col" className="px-3 py-3 w-[70px]">
                Order
              </th>
              <th scope="col" className="px-3 py-3 w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.reports.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-400 italic">
                  No reports yet. Click "Add" to create one.
                </td>
              </tr>
            )}
            {data.reports.map((report, index) => (
              <tr
                key={report.id}
                className={`${index % 2 ? 'bg-gray-50' : 'bg-white'} border-b dark:bg-gray-800 dark:border-gray-700`}
              >
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{index + 1}</td>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{report.title}</td>
                <td>
                  <select
                    value={index}
                    onChange={(e) => onChangeOrder(index, e.target.value)}
                    disabled={isLoading}
                    className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                  >
                    {orderOptions.map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <span className="flex gap-2">
                    <span
                      className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                      onClick={() => onEdit(report)}
                      title="Edit"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span
                      className="text-red-800 p-1 hover-shadow1 cursor-pointer rounded"
                      onClick={() => onDelete(index)}
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
